import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getUserPreferences, updateUserPreferences as apiUpdateUserPreferences } from '../services/api';

const UserPreferencesContext = createContext();

export const UserPreferencesProvider = ({ children, onBeforeRegionChange, controlLanguage = true }) => {
  const { i18n } = useTranslation();
  
  // Initialize from localStorage with fallbacks
  const [preferences, setPreferences] = useState({
    language: localStorage.getItem('language') || 'en',
    region: localStorage.getItem('region') || 'ua',
    showDualPrices: true,
    displayCurrency: localStorage.getItem('displayCurrency') || 'USD' // USD/UAH for Ukraine
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load user preferences from backend on mount
  useEffect(() => {
    const loadUserPreferences = async () => {
      try {
        setLoading(true);
        // Get user preferences from backend
        const response = await getUserPreferences();
        
        if (response.data) {
          const backendPrefs = {
            language: response.data.language || preferences.language,
            region: response.data.region || preferences.region,
            showDualPrices: true, // Default for now
            displayCurrency: response.data.displayCurrency || preferences.displayCurrency
          };
          
          setPreferences(backendPrefs);
          
          // Update localStorage
          localStorage.setItem('language', backendPrefs.language);
          localStorage.setItem('region', backendPrefs.region);
          localStorage.setItem('displayCurrency', backendPrefs.displayCurrency);
          
          // Update i18n language unless language control is disabled by parent
          if (controlLanguage) {
            i18n.changeLanguage(backendPrefs.language);
          }
        } else {
          // No backend data, use localStorage values
          if (controlLanguage) {
            i18n.changeLanguage(preferences.language);
          }
        }
      } catch (err) {
        console.error('Failed to load user preferences from backend:', err);
        // Fall back to localStorage values
        if (controlLanguage) {
          i18n.changeLanguage(preferences.language);
        }
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    loadUserPreferences();
  }, []);

  // Update preferences function
  const updatePreferences = useCallback(async (newPrefs) => {
    try {
      const updatedPrefs = { ...preferences, ...newPrefs };
      
      // Update local state
      setPreferences(updatedPrefs);
      
      // Update localStorage
      if (newPrefs.language !== undefined) {
        localStorage.setItem('language', newPrefs.language);
        if (controlLanguage) {
          i18n.changeLanguage(newPrefs.language);
        }
      }
      
      if (newPrefs.region !== undefined) {
        localStorage.setItem('region', newPrefs.region);
      }
      
      if (newPrefs.displayCurrency !== undefined) {
        localStorage.setItem('displayCurrency', newPrefs.displayCurrency);
      }
      
      // Sync with backend
      await apiUpdateUserPreferences({
        language: updatedPrefs.language,
        region: updatedPrefs.region,
        displayCurrency: updatedPrefs.displayCurrency
      });
      
    } catch (err) {
      console.error('Failed to update preferences:', err);
      setError(err);
    }
  }, [preferences, i18n]);

  // Individual setter functions for convenience
  const setLanguage = useCallback((language) => {
    updatePreferences({ language });
  }, [updatePreferences]);

  const setRegion = useCallback(async (region) => {
    // Check if we need to confirm region change
    if (onBeforeRegionChange) {
      const shouldContinue = await onBeforeRegionChange(preferences.region, region);
      if (!shouldContinue) {
        return; // User cancelled the region change
      }
    }
    
    updatePreferences({ region });
  }, [updatePreferences, onBeforeRegionChange, preferences.region]);

  const setShowDualPrices = useCallback((showDualPrices) => {
    updatePreferences({ showDualPrices });
  }, [updatePreferences]);

  const setDisplayCurrency = useCallback((displayCurrency) => {
    updatePreferences({ displayCurrency });
  }, [updatePreferences]);

  const contextValue = {
    preferences,
    loading,
    error,
    updatePreferences,
    setLanguage,
    setRegion,
    setShowDualPrices,
    setDisplayCurrency,
    // Convenience getters
    language: preferences.language,
    region: preferences.region,
    showDualPrices: preferences.showDualPrices,
    displayCurrency: preferences.displayCurrency
  };

  return (
    <UserPreferencesContext.Provider value={contextValue}>
      {children}
    </UserPreferencesContext.Provider>
  );
};

export const useUserPreferences = () => {
  const context = useContext(UserPreferencesContext);
  if (context === undefined) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider');
  }
  return context;
};

export default UserPreferencesContext;